import { useTender } from '../contexts/TenderContext';
import { useEffect } from 'react';

export interface RoadsAndSewersSettings {
  defaultPipeType: 'Clay' | 'Plastic' | 'Concrete';
  defaultBackfillType: 'Type 1' | '6F2';
  overheadAndProfit: number;
  preliminaries: number;
  discount: number;
}

export interface GroundworksSettings {
  overheadAndProfit: number;
  preliminaries: number;
  discount: number;
}

export interface GlobalSettings {
  roadsAndSewers: RoadsAndSewersSettings;
  groundworks: GroundworksSettings;
}

/**
 * Hook to manage global settings from TenderContext
 * This ensures global settings are stored with the tender data, not separately
 */
export const useGlobalSettings = () => {
  const { state, updateField } = useTender();

  // Remove old separate localStorage key on first mount
  useEffect(() => {
    const oldStorageKey = 'mj-estimator-global-settings';
    if (localStorage.getItem(oldStorageKey)) {
      console.log('Removing old global settings storage key - now using TenderContext');
      localStorage.removeItem(oldStorageKey);
    }
  }, []);

  // Map TenderContext globalSettings to the expected format
  const pipeTypeMap: { [key: string]: 'Clay' | 'Plastic' | 'Concrete' } = {
    'clay': 'Clay',
    'plastic': 'Plastic',
    'concrete': 'Concrete'
  };

  const backfillTypeMap: { [key: string]: 'Type 1' | '6F2' } = {
    'type1': 'Type 1',
    '6F2': '6F2'
  };

  const settings: GlobalSettings = {
    roadsAndSewers: {
      defaultPipeType: pipeTypeMap[state.globalSettings.pipeType] || 'Plastic',
      defaultBackfillType: backfillTypeMap[state.globalSettings.backfillType] || 'Type 1',
      overheadAndProfit: state.globalSettings.roadsAndSewers?.overheadAndProfit || 0,
      preliminaries: state.globalSettings.roadsAndSewers?.preliminaries || 0,
      discount: state.globalSettings.roadsAndSewers?.discount || 0,
    },
    groundworks: {
      overheadAndProfit: state.globalSettings.groundworks?.overheadAndProfit || 0,
      preliminaries: state.globalSettings.groundworks?.preliminaries || 0,
      discount: state.globalSettings.groundworks?.discount || 0,
    },
  };

  // Update settings in TenderContext
  const updateSettings = (newSettings: Partial<GlobalSettings>) => {
    const reversePipeTypeMap: { [key: string]: 'clay' | 'plastic' | 'concrete' } = {
      'Clay': 'clay',
      'Plastic': 'plastic',
      'Concrete': 'concrete'
    };

    const reverseBackfillTypeMap: { [key: string]: 'type1' | '6F2' } = {
      'Type 1': 'type1',
      '6F2': '6F2'
    };

    const updatedGlobalSettings = {
      ...state.globalSettings,
      pipeType: newSettings.roadsAndSewers?.defaultPipeType
        ? reversePipeTypeMap[newSettings.roadsAndSewers.defaultPipeType]
        : state.globalSettings.pipeType,
      backfillType: newSettings.roadsAndSewers?.defaultBackfillType
        ? reverseBackfillTypeMap[newSettings.roadsAndSewers.defaultBackfillType]
        : state.globalSettings.backfillType,
      roadsAndSewers: {
        ...state.globalSettings.roadsAndSewers,
        ...newSettings.roadsAndSewers,
      },
      groundworks: {
        ...state.globalSettings.groundworks,
        ...newSettings.groundworks,
      },
    };

    updateField('globalSettings', updatedGlobalSettings);
  };

  // Update specific job type settings
  const updateRoadsAndSewersSettings = (newSettings: Partial<RoadsAndSewersSettings>) => {
    const reversePipeTypeMap: { [key: string]: 'clay' | 'plastic' | 'concrete' } = {
      'Clay': 'clay',
      'Plastic': 'plastic',
      'Concrete': 'concrete'
    };

    const reverseBackfillTypeMap: { [key: string]: 'type1' | '6F2' } = {
      'Type 1': 'type1',
      '6F2': '6F2'
    };

    const updatedGlobalSettings = {
      ...state.globalSettings,
      pipeType: newSettings.defaultPipeType
        ? reversePipeTypeMap[newSettings.defaultPipeType]
        : state.globalSettings.pipeType,
      backfillType: newSettings.defaultBackfillType
        ? reverseBackfillTypeMap[newSettings.defaultBackfillType]
        : state.globalSettings.backfillType,
      roadsAndSewers: {
        ...state.globalSettings.roadsAndSewers,
        overheadAndProfit: newSettings.overheadAndProfit ?? state.globalSettings.roadsAndSewers.overheadAndProfit,
        preliminaries: newSettings.preliminaries ?? state.globalSettings.roadsAndSewers.preliminaries,
        discount: newSettings.discount ?? state.globalSettings.roadsAndSewers.discount,
      },
    };

    updateField('globalSettings', updatedGlobalSettings);
  };

  const updateGroundworksSettings = (newSettings: Partial<GroundworksSettings>) => {
    const updatedGlobalSettings = {
      ...state.globalSettings,
      groundworks: {
        ...state.globalSettings.groundworks,
        ...newSettings,
      },
    };

    updateField('globalSettings', updatedGlobalSettings);
  };

  // Reset settings to default
  const resetSettings = () => {
    const defaultSettings = {
      pipeType: 'plastic' as const,
      backfillType: 'type1' as const,
      allowOverrides: false,
      roadsAndSewers: {
        overheadAndProfit: 0,
        preliminaries: 0,
        discount: 0,
      },
      groundworks: {
        overheadAndProfit: 0,
        preliminaries: 0,
        discount: 0,
      },
    };

    updateField('globalSettings', defaultSettings);
  };

  return {
    settings,
    updateSettings,
    updateRoadsAndSewersSettings,
    updateGroundworksSettings,
    resetSettings,
  };
};

// Legacy compatibility function for components that still use the old format
export const useLegacyGlobalSettings = () => {
  const { settings, updateRoadsAndSewersSettings } = useGlobalSettings();

  // Return a legacy-compatible interface
  const legacySettings = {
    defaultPipeType: settings.roadsAndSewers.defaultPipeType,
    defaultBackfillType: settings.roadsAndSewers.defaultBackfillType,
    overheadAndProfit: settings.roadsAndSewers.overheadAndProfit,
    preliminaries: settings.roadsAndSewers.preliminaries,
  };

  const updateSettings = (newSettings: any) => {
    updateRoadsAndSewersSettings(newSettings);
  };

  const resetSettings = () => {
    updateRoadsAndSewersSettings({
      defaultPipeType: 'Plastic',
      defaultBackfillType: 'Type 1',
      overheadAndProfit: 0,
      preliminaries: 0,
      discount: 0,
    });
  };

  return {
    settings: legacySettings,
    updateSettings,
    resetSettings,
  };
};
